import { Loc } from '../engine/core/localization.js';
import { debugLog, debugTable, DEBUG, DEFAULT_ERROR_MSG } from '../../tools/debug.js';

// Comment class representing a single comment in the conversation
export class Comment
{
    constructor ({id, userId, username, message, created_at, parentId = null, cadReference = null, isEdited = false, updated_at = null, attached_to = null, product_id = null, mode = 'text', coordinates = null, data = null, is_deleted = false} = {})
    {
        this.id = id;
        this.userId = userId;
        this.username = username;
        this.message = message;
        this.created_at = created_at;
        this.parentId = parentId; // For replies
        this.cadReference = cadReference; // Legacy: Reference to CAD components/parts (now stored in data field)
        this.mentions = this.extractMentions(message); // Extract @mentions from message
        this.isEdited = isEdited;
        this.updated_at = updated_at;

        // New API fields
        this.attached_to = attached_to; // File ID that this comment is attached to
        this.product_id = product_id; // Product ID if applicable
        this.mode = mode; // Comment mode (text, etc.)
        this.coordinates = coordinates; // Position coordinates [x, y]
        this.data = data; // Additional data like camera position and translation
        this.is_deleted = is_deleted; // Soft delete flag
    }

    toAPI(){
        return {
            id: this.id,
            owner_id: this.userId,
            attached_to: this.attached_to,
            product_id: this.product_id,
            content: this.message,
            mode: this.mode,
            coordinates: this.coordinates,
            parent_id: this.parentId,
            data: this.data,
            is_deleted: this.is_deleted,
            created_at: this.created_at,
            updated_at: this.updated_at
        }
    }

    static formatToAPI(data){
        return {
            id: data?.id,
            owner_id: data?.owner_id ?? data?.userId,
            attached_to: data?.attached_to,
            product_id: data?.product_id ?? data?.productId,
            content: data?.content ?? data?.message,
            coordinates: data?.coordinates,
            parent_id: data?.parent_id ?? data?.parentId,
            data: data?.data,
            created_at: data?.created_at,
            updated_at: data?.updated_at
        }
    }

    static formatFromAPI(data){
        // Handle cadReference migration: if API has cad_reference, move it to data field
        let processedData = data?.data;
        const legacyCadRef = data?.cadReference ?? data?.cad_reference;

        if (legacyCadRef && !processedData?.cadReference) {
            processedData = {
                ...processedData,
                cadReference: legacyCadRef
            };
        }

        return new Comment({
            id: data?.id,
            userId: data?.userId ?? data?.owner_id,
            username: data?.username,
            message: data?.message ?? data?.content,
            created_at: data?.created_at ? new Date(data.created_at) : null,
            parentId: data?.parentId ?? data?.parent_id,
            cadReference: legacyCadRef, // Keep for backward compatibility
            isEdited: data?.isEdited ?? false,
            updated_at: data?.updated_at ? new Date(data.updated_at) : null,
            attached_to: data?.attached_to,
            product_id: data?.product_id,
            mode: data?.mode ?? 'text',
            coordinates: data?.coordinates,
            data: processedData,
            is_deleted: data?.is_deleted ?? false
        });
    }

    extractMentions (message)
    {
        const mentionRegex = /@(\w+)/g;
        const mentions = [];
        let match;
        while ((match = mentionRegex.exec(message)) !== null) {
            mentions.push(match[1]);
        }
        return mentions;
    }

    edit (newMessage)
    {
        this.message = newMessage;
        this.mentions = this.extractMentions(newMessage);
        this.isEdited = true;
        this.updated_at = new Date();
    }

    getFormattedTimestamp ()
    {
        const now = new Date();
        const diff = now - this.created_at;
        const minutes = Math.floor(diff / (1000 * 60));
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));

        if (minutes < 1) return Loc('Just now');
        if (minutes < 60) return `${minutes}${Loc('m ago')}`;
        if (hours < 24) return `${hours}${Loc('h ago')}`;
        if (days < 7) return `${days}${Loc('d ago')}`;

        return this.created_at?.toLocaleDateString();
    }

    isReply ()
    {
        return this.parentId !== null;
    }
}

// CAD Reference class for linking comments to specific parts/components
export class CadReference
{
    constructor (type, elementId, elementName, viewState = null)
    {
        this.type = type; // 'mesh', 'material', 'node', 'face', etc.
        this.elementId = elementId;
        this.elementName = elementName;
        this.viewState = viewState; // Camera position, selection state, etc.
    }
}

// User class for managing comment participants
export class User
{
    constructor (id, username, displayName = null, avatar = null, isOnline = false)
    {
        this.id = id;
        this.username = username;
        this.displayName = displayName || username;
        this.avatar = avatar;
        this.isOnline = isOnline;
        this.lastSeen = new Date();
    }
}

export class AuthUser extends User {
    constructor ({id, username, displayName, avatar, isOnline, token} = {})
    {
        super(id, username, displayName, avatar, isOnline);
        this.token = token;
    }
}

// Main comments management class
export class CommentsManager
{
    constructor (apiConfig = null)
    {
        this.comments = new Map(); // Map of comment ID to Comment object
        this.users = new Map(); // Map of user ID to User object
        this.currentUser = null;
        this.commentIdCounter = 1;
        this.eventListeners = new Map(); // For custom events

        // API configuration
        this.apiConfig = apiConfig || {};

        this.isOnline = navigator.onLine;
        this.pendingOperations = []; // Queue for offline operations

        // Setup online/offline listeners
        this.setupNetworkListeners();

        // Initialize data asynchronously
        this.initialize().catch(error => {
            console.error('Failed to initialize comments manager:', error);
        });
    }

    async initialize ()
    {
        if (this.apiConfig.enabled && this.isOnline) {
            try {
                await this.loadFromAPI();
            } catch (error) {
                console.warn('Failed to load from API, using demo data:', error);
                // this.initializeDemoData();
            }
        } else {
            // this.initializeDemoData();
            // TODO show message saying not online or not configured
        }
    }

    redirectToLogin ()
    {
        const NEWTAB = this.apiConfig.openLoginInNewTab;
        const loginUrl = `${this.apiConfig.loginUrl}?${this.apiConfig.redirectKeyword}=${encodeURIComponent(window.location.href)}`;
        debugLog(">> redirect To", loginUrl);

        // Give option to open in new tab
        if (NEWTAB) {
            window.open(loginUrl, '_blank', 'noopener,noreferrer');
        } else {
            window.location.href = loginUrl;
        }
    }

    setupNetworkListeners ()
    {
        window.addEventListener('online', () => {
            console.log('Network connection restored');
            this.isOnline = true;
            this.syncPendingOperations();
        });

        window.addEventListener('offline', () => {
            console.log('Network connection lost');
            this.isOnline = false;
        });
    }

    // API Methods
    async apiRequest (endpoint, options = {}, {isEndpointFullUrl = false, mockAuth = false} = {})
    {
        if (!this.apiConfig.enabled) {
            throw new Error('API not enabled');
        }

        if(mockAuth){
            return {
                "user": {
                    "id": "3deac4be-3652-4cc4-9cc2-8cf968489b37",
                    "app_metadata": {
                        "provider": "email",
                        "providers": [
                            "email"
                        ]
                    },
                    "user_metadata": {
                        "email": "amane@productflo.io",
                        "email_verified": true,
                        "first_name": "Amane",
                        "last_name": "Hosanna",
                        "phone_verified": false,
                        "sub": "3deac4be-3652-4cc4-9cc2-8cf968489b37"
                    },
                    "aud": "authenticated",
                    "confirmation_sent_at": "2025-05-06T21:20:09.916998Z",
                    "recovery_sent_at": "2025-07-22T16:56:56.076098Z",
                    "email_change_sent_at": null,
                    "new_email": null,
                    "new_phone": null,
                    "invited_at": null,
                    "action_link": null,
                    "email": "amane@productflo.io",
                    "phone": "",
                    "created_at": "2025-05-06T21:20:09.906825Z",
                    "confirmed_at": "2025-05-06T21:20:21.047142Z",
                    "email_confirmed_at": "2025-05-06T21:20:21.047142Z",
                    "phone_confirmed_at": null,
                    "last_sign_in_at": "2025-08-01T09:07:33.040823Z",
                    "role": "authenticated",
                    "updated_at": "2025-08-01T09:07:33.044750Z",
                    "identities": [
                        {
                            "id": "3deac4be-3652-4cc4-9cc2-8cf968489b37",
                            "identity_id": "fb05d212-27d0-4075-a90a-165a60494d0e",
                            "user_id": "3deac4be-3652-4cc4-9cc2-8cf968489b37",
                            "identity_data": {
                                "email": "amane@productflo.io",
                                "email_verified": true,
                                "first_name": "Amane",
                                "last_name": "Hosanna",
                                "phone_verified": false,
                                "sub": "3deac4be-3652-4cc4-9cc2-8cf968489b37",
                                "username": null
                            },
                            "provider": "email",
                            "created_at": "2025-05-06T21:20:09.912074Z",
                            "last_sign_in_at": "2025-05-06T21:20:09.912021Z",
                            "updated_at": "2025-05-06T21:20:09.912074Z"
                        }
                    ],
                    "is_anonymous": false,
                    "factors": null
                }
            }
        }

        const url = isEndpointFullUrl ? endpoint :  `${this.apiConfig.baseUrl}${endpoint}`;
        const defaultOptions = {
            headers: {
                ...this.apiConfig.headers,
                ...(this.currentUser ? { 'Authorization': `Bearer ${this.currentUser.token}` } : {})
            }
        };

        const reqOptions = {...options, ...defaultOptions, headers: {...defaultOptions.headers, ...options.headers}};
        debugLog('COMMENT::apiRequest', url, reqOptions);

        const response = await fetch(url, reqOptions);

        debugLog('COMMENT::fetch <<', response);


        if (!response.ok) {
            throw new Error(`API request failed: ${response.status} ${response.statusText}`);
        }

        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
            const jsonResponse = await response.json();
            debugLog("<< response json", jsonResponse);
            return jsonResponse;
        }

        if (contentType && contentType.includes('text/html')) {
            throw new Error(DEBUG ? 'Received HTML response instead of expected format' : DEFAULT_ERROR_MSG);
        }

        const textResponse = await response.text();
        if (textResponse.trim().toLowerCase().startsWith('<')) {
            throw new Error(DEBUG ? 'Received HTML response instead of expected format' : DEFAULT_ERROR_MSG);
        }
        debugLog("<< response text", textResponse);
        return textResponse();
    }


    async loadFromAPI ()
    {
        console.log('Loading comments from API...');
        // Load auth user
        // Check if access_token cookie is set
        debugLog("-->><< document.cookie >><<--")
        debugTable(document.cookie.split('; '))

        const accessTokenCookie = document.cookie.split('; ').find(row => row.startsWith('access_token='));
        if (!accessTokenCookie) {
            debugLog('No access token found in cookies', 'accessTokenCookie', accessTokenCookie);
            return;
        }
        const accessToken = accessTokenCookie.split('=')[1];
        debugLog(">>ACCESS_TOKEN<<", accessToken)

        try {
            // const authUserData = await this.apiRequest(this.apiConfig.endpoints.auth, {
            const authUserData = await this.apiRequest(this.apiConfig.endpoints.login, {
                method: 'GET',
                // body: JSON.stringify({
                //     access_token: accessToken
                // }),
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            }, { isEndpointFullUrl : true});
            if(!authUserData){
                debugLog('No auth user returned from API');
                return;
            }

            const newUser = new AuthUser({
                id: authUserData.user.id,
                username: authUserData.user.user_metadata.username,
                displayName: authUserData.user.user_metadata.first_name + " " + authUserData.user.user_metadata.last_name,
                avatar: authUserData.user.user_metadata.avatar,
                isOnline: true,
                token: accessToken
            });

            // Set current user and fire event
            this.setCurrentUser(newUser.id);
            this.currentUser = newUser; // Also set directly since setCurrentUser looks in users map
        } catch (error) {
            debugLog('Failed to authenticate user:', error);
            return;
        }

        // Load users
        const usersData = [];
        // const usersData = await this.apiRequest(this.apiConfig.endpoints.users);
        // if (usersData && usersData.users) {
        //     usersData.users.forEach(userData => {
        //         const user = new User(
        //             userData.id,
        //             userData.username,
        //             userData.displayName,
        //             userData.avatar,
        //             userData.isOnline
        //         );
        //         this.users.set(user.id, user);
        //     });
        // }

        // Load comments
        const commentsData = await this.apiRequest(this.apiConfig.endpoints.comments);
        debugLog("<< commentsFromAPI", commentsData);
        if (commentsData && commentsData.comments) {
            commentsData.comments.forEach(commentData => {
                const comment = new Comment({
                    id: commentData.id,
                    userId: commentData.userId,
                    username: commentData.username,
                    message: commentData.message,
                    created_at: new Date(commentData.created_at),
                    parentId: commentData.parentId,
                    cadReference: commentData.cadReference,
                    isEdited: commentData.isEdited || false,
                    updated_at: commentData.editTimestamp ? new Date(commentData.editTimestamp) : null,
                    attached_to: commentData.attached_to,
                    product_id: commentData.product_id,
                    mode: commentData.mode || 'text',
                    coordinates: commentData.coordinates,
                    data: commentData.data,
                    is_deleted: commentData.is_deleted || false
                });
                this.comments.set(comment.id, comment);
            });

            // Update counter
            if (commentsData.nextId) {
                this.commentIdCounter = commentsData.nextId;
            }
        }else{
            // Normal comments
            commentsData.forEach(commentData => {
                const comment = Comment.formatFromAPI(commentData);
                this.comments.set(comment.id, comment);
            });
            debugLog("<< comentsSet", this.comments)
        }

        this.fireEvent('commentsLoaded', { source: 'api' });
    }

    async syncPendingOperations ()
    {
        if (!this.apiConfig.enabled || !this.isOnline || this.pendingOperations.length === 0) {
            return;
        }

        console.log(`Syncing ${this.pendingOperations.length} pending operations...`);

        const operations = [...this.pendingOperations];
        this.pendingOperations = [];

        for (const operation of operations) {
            try {
                await this.executeOperation(operation);
            } catch (error) {
                console.error('Failed to sync operation:', operation, error);
                // Re-queue failed operations
                this.pendingOperations.push(operation);
            }
        }
    }

    async executeOperation (operation)
    {
        switch (operation.type) {
            case 'addComment':
                return await this.apiAddComment(operation.data);
            case 'editComment':
                return await this.apiEditComment(operation.data.commentId, operation.data.newMessage);
            case 'deleteComment':
                return await this.apiDeleteComment(operation.data.commentId);
            default:
                throw new Error(`Unknown operation type: ${operation.type}`);
        }
    }

    queueOperation (type, data)
    {
        this.pendingOperations.push({
            type,
            data,
            created_at: new Date()
        });
    }

    initializeDemoData ()
    {
        // Add demo users
        this.addUser(new User('user1', 'john_doe', 'John Doe', null, true));
        this.addUser(new User('user2', 'jane_smith', 'Jane Smith', null, false));
        this.addUser(new User('user3', 'mike_wilson', 'Mike Wilson', null, true));

        // Set current user
        this.currentUser = new AuthUser({...this.users.get('user1'), token: '1234567890'});

        // Add demo comments
        const comment1 = this.addComment(
            'user2',
            'The main body geometry looks good, but I think we need to adjust the tolerances on the mounting holes.',
            new CadReference('mesh', 'mesh_001', 'Main Body')
        );

        this.addComment(
            'user1',
            '@jane_smith Good point! What tolerance range are you thinking?',
            null,
            comment1.id
        );

        this.addComment(
            'user3',
            'I agree with @jane_smith. Also, can we review the material assignment for the handle?',
            new CadReference('material', 'mat_002', 'Handle Material')
        );
    }

    // User management
    addUser (user)
    {
        this.users.set(user.id, user);
        this.fireEvent('userAdded', { user });
    }

    getUser (userId)
    {
        return this.users.get(userId);
    }

    getCurrentUser ()
    {
        return this.currentUser;
    }

    getCurrentUserId ()
    {
        return this.currentUser?.id;
    }
    getAccessToken ()
    {
        return this.currentUser?.token;
    }

    setCurrentUser (userId)
    {
        const previousUser = this.currentUser;
        this.currentUser = this.users.get(userId);

        // Fire event if user actually changed
        if (previousUser?.id !== this.currentUser?.id) {
            this.fireEvent('currentUserChanged', {
                user: this.currentUser,
                previousUser: previousUser
            });
        }
    }

    // Comment management
    async addComment ({userId, message, cadReference = null, parentId = null, attached_to = null, product_id = null, mode = 'text', coordinates = null, data = null, is_deleted = false} = {})
    {
        debugLog('COMMENT::addComment', userId, message, data, parentId);
        const commentData = {
            userId,
            message,
            cadReference, // Keep for backward compatibility
            parentId,
            attached_to,
            product_id,
            mode,
            coordinates,
            data,
            is_deleted
        };

        // Try API first if enabled and online
        if (this.apiConfig.enabled && this.isOnline) {
            try {
                const result = await this.apiAddComment(commentData);
                return result;
            } catch (error) {
                console.error('API addComment failed, falling back to local:', error);
                // this.queueOperation('addComment', commentAPIData);
                // TODO fail depending on error type
                throw error;
            }
        } else if (this.apiConfig.enabled && !this.isOnline) {
            // Queue for later sync
            this.queueOperation('addComment', commentData);
        }

        // Local fallback
        return this.localAddComment(userId, message, cadReference, parentId, attached_to, product_id, mode, coordinates, data, is_deleted);
    }

    async apiAddComment (commentData)
    {
        const commentAPIData = Comment.formatToAPI(commentData);
        const response = await this.apiRequest(this.apiConfig.endpoints.comments, {
            method: 'POST',
            body: JSON.stringify(commentAPIData)
        });


        const comment = Comment.formatFromAPI(response);
        console.log("<<<< apiAddComment", response, "comment", comment)

        this.comments.set(comment.id, comment);
        this.fireEvent('commentAdded', { comment, source: 'api' });

        if (comment.mentions.length > 0) {
            this.fireEvent('usersMentioned', { comment, mentions: comment.mentions });
        }

        return comment;
    }

    localAddComment (userId, message, cadReference = null, parentId = null, attached_to = null, product_id = null, mode = 'text', coordinates = null, data = null, is_deleted = false)
    {
        // Handle legacy cadReference parameter - merge into data if not already there
        let finalData = data;
        if (cadReference && !finalData?.cadReference) {
            finalData = {
                ...finalData,
                cadReference: cadReference
            };
        }

        const comment = new Comment({
            id: this.commentIdCounter++,
            userId,
            username: this.getUser(userId)?.username || 'Unknown',
            message,
            created_at: new Date(),
            parentId,
            cadReference, // Keep for backward compatibility
            attached_to,
            product_id,
            mode,
            coordinates,
            data: finalData,
            is_deleted
        });

        this.comments.set(comment.id, comment);
        this.fireEvent('commentAdded', { comment, source: 'local' });

        // Notify mentioned users
        if (comment.mentions.length > 0) {
            this.fireEvent('usersMentioned', { comment, mentions: comment.mentions });
        }

        return comment;
    }

    async editComment (commentId, newMessage)
    {
        const comment = this.comments.get(commentId);
        if (!comment || comment.userId !== this.currentUser?.id) {
            return false;
        }

        // Try API first if enabled and online
        if (this.apiConfig.enabled && this.isOnline) {
            try {
                await this.apiEditComment(commentId, newMessage);
                return true;
            } catch (error) {
                console.error('API editComment failed, falling back to local:', error);
                this.queueOperation('editComment', { commentId, newMessage });
            }
        } else if (this.apiConfig.enabled && !this.isOnline) {
            // Queue for later sync
            this.queueOperation('editComment', { commentId, newMessage });
        }

        // Local fallback
        comment.edit(newMessage);
        this.fireEvent('commentEdited', { comment, source: 'local' });
        return true;
    }

    async apiEditComment (commentId, newMessage)
    {
        await this.apiRequest(`${this.apiConfig.endpoints.comments}/${commentId}`, {
            method: 'PUT',
            body: JSON.stringify(Comment.formatToAPI({message: newMessage}))
        });

        const comment = this.comments.get(commentId);
        if (comment) {
            comment.edit(newMessage);
            this.fireEvent('commentEdited', { comment, source: 'api' });
        }
    }

    async deleteComment (commentId)
    {
        const comment = this.comments.get(commentId);
        if (!comment || comment.userId !== this.currentUser?.id) {
            return false;
        }

        // Try API first if enabled and online
        if (this.apiConfig.enabled && this.isOnline) {
            try {
                await this.apiDeleteComment(commentId);
                return true;
            } catch (error) {
                console.error('API deleteComment failed, falling back to local:', error);
                this.queueOperation('deleteComment', { commentId });
            }
        } else if (this.apiConfig.enabled && !this.isOnline) {
            // Queue for later sync
            this.queueOperation('deleteComment', { commentId });
        }

        // Local fallback
        this.comments.delete(commentId);
        this.fireEvent('commentDeleted', { commentId, source: 'local' });
        return true;
    }

    async apiDeleteComment (commentId)
    {
        await this.apiRequest(`${this.apiConfig.endpoints.comments}/${commentId}`, {
            method: 'DELETE'
        });

        this.comments.delete(commentId);
        this.fireEvent('commentDeleted', { commentId, source: 'api' });
    }

    // Refresh comments from API
    async refreshComments ()
    {
        if (!this.apiConfig.enabled || !this.isOnline) {
            return false;
        }

        try {
            this.comments.clear();
            await this.loadFromAPI();
            this.fireEvent('commentsRefreshed', { source: 'api' });
            return true;
        } catch (error) {
            console.error('Failed to refresh comments:', error);
            return false;
        }
    }

    getComment (commentId)
    {
        return this.comments.get(commentId);
    }

    // Get comments in chronological order
    getComments ()
    {
        return Array.from(this.comments.values()).sort((a, b) => a.created_at - b.created_at);
    }

    // Get top-level comments (not replies)
    getTopLevelComments ()
    {
        return this.getComments().filter(comment => !comment.isReply());
    }

    // Get replies to a specific comment
    getReplies (parentId)
    {
        return this.getComments().filter(comment => comment.parentId === parentId);
    }

    // Get comments that mention a specific user
    getMentionsForUser (username)
    {
        return this.getComments().filter(comment =>
            comment.mentions.includes(username)
        );
    }

    // Get comments with CAD references
    getCommentsWithCadReferences ()
    {
        return this.getComments().filter(comment => comment.cadReference !== null);
    }

    // Get comments referencing a specific CAD element
    getCommentsForCadElement (elementId)
    {
        return this.getComments().filter(comment =>
            comment.cadReference && comment.cadReference.elementId === elementId
        );
    }

    // Event system for UI updates
    addEventListener (eventType, callback)
    {
        if (!this.eventListeners.has(eventType)) {
            this.eventListeners.set(eventType, []);
        }
        this.eventListeners.get(eventType).push(callback);
    }

    removeEventListener (eventType, callback)
    {
        const listeners = this.eventListeners.get(eventType);
        if (listeners) {
            const index = listeners.indexOf(callback);
            if (index > -1) {
                listeners.splice(index, 1);
            }
        }
    }

    fireEvent (eventType, data)
    {
        const listeners = this.eventListeners.get(eventType);
        if (listeners) {
            listeners.forEach(callback => callback(data));
        }
    }

    // Search functionality
    searchComments (query)
    {
        const lowerQuery = query.toLowerCase();
        return this.getComments().filter(comment =>
            comment.message.toLowerCase().includes(lowerQuery) ||
            comment.username.toLowerCase().includes(lowerQuery)
        );
    }

    // Export/Import for persistence
    exportComments ()
    {
        return {
            comments: Array.from(this.comments.entries()),
            users: Array.from(this.users.entries()),
            currentUserId: this.currentUser?.id,
            commentIdCounter: this.commentIdCounter
        };
    }

    importComments (data)
    {
        this.comments.clear();
        this.users.clear();

        if (data.comments) {
            data.comments.forEach(([id, commentData]) => {
                const comment = new Comment({
                    id: commentData.id,
                    userId: commentData.userId,
                    username: commentData.username,
                    message: commentData.message,
                    created_at: new Date(commentData.created_at),
                    parentId: commentData.parentId,
                    cadReference: commentData.cadReference,
                    isEdited: commentData.isEdited,
                    updated_at: commentData.editTimestamp ? new Date(commentData.editTimestamp) : null,
                    attached_to: commentData.attached_to,
                    product_id: commentData.product_id,
                    mode: commentData.mode || 'text',
                    coordinates: commentData.coordinates,
                    data: commentData.data,
                    is_deleted: commentData.is_deleted || false
                });
                this.comments.set(id, comment);
            });
        }

        if (data.users) {
            data.users.forEach(([id, userData]) => {
                const user = new User(
                    userData.id,
                    userData.username,
                    userData.displayName,
                    userData.avatar,
                    userData.isOnline
                );
                user.lastSeen = new Date(userData.lastSeen);
                this.users.set(id, user);
            });
        }

        if (data.currentUserId) {
            this.currentUser = this.users.get(data.currentUserId);
        }

        if (data.commentIdCounter) {
            this.commentIdCounter = data.commentIdCounter;
        }

        this.fireEvent('commentsImported', { data });
    }
}
// Test change
// Another test change Thu Jul 31 16:19:59 GMT 2025
// Test with nodemon Thu Jul 31 16:22:01 GMT 2025
// Another nodemon test Thu Jul 31 16:23:22 GMT 2025
